import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { defaultDateRange, useAuthConfig } from "@theprelude/core";
import {
  authorColumn,
  benchmarkColumn,
  protectedColumn,
  publishedColumn,
  scheduledColumn,
  sourceIdColumn,
  threatNameColumn,
  threatIdColumn,
} from "@/components/table/columns";
import { useTable } from "@/components/table/hooks";
import { VirtualTable } from "@/components/table/virtual-table";
import {
  DashboardThreat,
  dashboardThreatsQuery,
} from "@/hooks/quickview-queries";
import { ensureAuth } from "@/lib/auth";
import { authorizeFeatures } from "@/lib/authorize-feature";
import { getActivityQuery } from "@/lib/validators";

export const Route = createFileRoute("/_app/dashboard/threats")({
  loaderDeps: () => {
    return { query: getActivityQuery() };
  },
  beforeLoad: async (opts) => {
    await authorizeFeatures(opts.context, ["detect"]);
  },
  loader: async (opts) => {
    return opts.context.queryClient.ensureQueryData(
      dashboardThreatsQuery(
        await ensureAuth(opts.context.auth),
        opts.deps.query,
      ),
    );
  },
  component: ThreatsPage,
});

const columnHelper = createColumnHelper<DashboardThreat>();
const columns = [
  columnHelper.accessor("threatId", threatIdColumn),
  columnHelper.accessor("source_id", sourceIdColumn),
  columnHelper.accessor("threat", threatNameColumn),
  columnHelper.accessor("author", authorColumn),
  columnHelper.accessor("published", publishedColumn),
  columnHelper.accessor("protected", protectedColumn),
  columnHelper.accessor("benchmark", benchmarkColumn),
  columnHelper.accessor("status", scheduledColumn),
];

function ThreatsPage() {
  const authConfig = useAuthConfig();
  const query = useSuspenseQuery(
    dashboardThreatsQuery(authConfig, {
      start: defaultDateRange.start.toISOString(),
      finish: defaultDateRange.finish.toISOString(),
    }),
  );

  const table = useTable({
    name: "dashboard-threats",
    data: query.data ?? [],
    columns,
    initialState: {
      sorting: [{ id: publishedColumn.id, desc: true }],
      columnVisibility: { [threatIdColumn.id]: false },
    },
  });

  return <VirtualTable showFilter table={table} />;
}
